import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Json, Tables } from "@how/database";
import { IMPORT_LIMITS, type ImportRow } from "./csv";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type ImportRecord = Tables<"imports">;

/** This church's recent imports, newest first, so an import in progress can be reopened after the tab was closed. */
export function useImports() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["imports", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ImportRecord[]> => {
      const { data, error } = await supabase
        .from("imports")
        .select("*")
        .eq("tenant_id", tenantId ?? "")
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
  });
}

/** One import. While it commits, it is polled every few seconds so the progress bar moves on its own. */
export function useImport(importId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["import", tenantId, importId],
    enabled: importId !== null,
    refetchInterval: (query) => (query.state.data?.status === "committing" ? 3000 : false),
    queryFn: async (): Promise<ImportRecord> => {
      const { data, error } = await supabase
        .from("imports")
        .select("*")
        .eq("id", importId ?? "")
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export type ProblemRow = Pick<
  Tables<"import_rows">,
  "row_number" | "first_name" | "last_name" | "email" | "status" | "errors"
>;

/** The rows that will not be imported, with the reason, as the church will want to fix them in its file. */
export function useImportProblems(importId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["import-problems", tenantId, importId],
    enabled: importId !== null,
    queryFn: async (): Promise<ProblemRow[]> => {
      const { data, error } = await supabase
        .from("import_rows")
        .select("row_number, first_name, last_name, email, status, errors")
        .eq("import_id", importId ?? "")
        .in("status", ["invalid", "duplicate"])
        .order("row_number")
        .limit(500);
      if (error) throw error;
      return data;
    },
  });
}

function useImportInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (importId?: string) => {
    await client.invalidateQueries({ queryKey: ["imports", tenantId] });
    if (importId !== undefined) {
      await client.invalidateQueries({ queryKey: ["import", tenantId, importId] });
      await client.invalidateQueries({ queryKey: ["import-problems", tenantId, importId] });
    }
  };
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

/**
 * Creates the import and sends its rows in chunks of 500, reporting progress after each, then closes staging so the
 * dry-run counts are fixed. Each chunk is its own short call, well inside the database's statement timeout.
 */
export function useStageImport() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useImportInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly fileName: string;
      readonly rows: readonly ImportRow[];
      readonly onProgress: (staged: number) => void;
    }): Promise<string> => {
      const created = await supabase.rpc("create_import", {
        p_tenant_id: tenantId ?? "",
        p_file_name: input.fileName,
      });
      failed(created.error);
      const importId = created.data as string;
      for (let start = 0; start < input.rows.length; start += IMPORT_LIMITS.chunk) {
        const chunk = input.rows.slice(start, start + IMPORT_LIMITS.chunk);
        const { error } = await supabase.rpc("stage_import_rows", {
          p_import_id: importId,
          // Each row is a plain object of strings and a row number, which is JSON as the function expects.
          p_rows: chunk as unknown as Json,
        });
        failed(error);
        input.onProgress(Math.min(start + chunk.length, input.rows.length));
      }
      const finished = await supabase.rpc("finish_import_staging", { p_import_id: importId });
      failed(finished.error);
      return importId;
    },
    onSuccess: async (importId) => {
      await invalidate(importId);
    },
  });
}

export function useCommitImport() {
  const supabase = useSupabase();
  const invalidate = useImportInvalidation();
  return useMutation({
    mutationFn: async (importId: string) => {
      const { error } = await supabase.rpc("commit_import", { p_import_id: importId });
      failed(error);
    },
    onSuccess: async (_result, importId) => {
      await invalidate(importId);
    },
  });
}

export function useDeleteImport() {
  const supabase = useSupabase();
  const invalidate = useImportInvalidation();
  return useMutation({
    mutationFn: async (importId: string) => {
      const { error } = await supabase.rpc("delete_import", { p_import_id: importId });
      failed(error);
    },
    onSuccess: async (_result, importId) => {
      await invalidate(importId);
    },
  });
}
